import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RhythmPrintViz from '@/components/RhythmPrintViz';
import ProgressRing from '@/components/ProgressRing';
import MetricChip from '@/components/MetricChip';
import StatusBadge from '@/components/StatusBadge';
import ContinuousAuthWidget from '@/components/ContinuousAuthWidget';
import Logo from '@/components/Logo';
import type { ActivityEntry, ProfileMetrics } from '@/types/api';
import { API_ENDPOINTS } from '@/types/api';
import { apiClient } from '@/lib/api-client';

/**
 * Dashboard — Overview page showing RhythmPrint, profile health,
 * recent activity, metrics, and device info.
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const [profileHealth, setProfileHealth] = useState<number | null>(null);
  const [metrics, setMetrics] = useState<ProfileMetrics | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [embedding, setEmbedding] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = apiClient.getAccessToken();
    if (!token) {
      navigate('/login');
      return;
    }

    (async () => {
      setIsLoading(true);
      try {
        const [healthResp, metricsResp, activityResp] = await Promise.all([
          apiClient.get<{ confidence: number; stability: number; accept_rate: number; needs_recalibration: boolean; total_auth_attempts: number }>(API_ENDPOINTS.PROFILE_HEALTH),
          apiClient.get<ProfileMetrics>(API_ENDPOINTS.ACTIVITY_METRICS),
          apiClient.get<ActivityEntry[]>(API_ENDPOINTS.ACTIVITY_RECENT),
        ]);

        // Map health response - use confidence or accept_rate for "health" percentage
        const health = healthResp.data;
        setProfileHealth(Math.round((health.confidence ?? health.accept_rate ?? 0.85) * 100));
        setMetrics(metricsResp.data);
        setActivity(activityResp.data);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      }

      // Fetch profile for RhythmPrint embedding
      try {
        const profileResp = await apiClient.get<{ template_embedding?: number[] }>(API_ENDPOINTS.PROFILE_ME);
        if (profileResp.data.template_embedding && profileResp.data.template_embedding.length > 0) {
          setEmbedding(profileResp.data.template_embedding);
        } else {
          // Generate deterministic placeholder from profile data
          setEmbedding(Array.from({ length: 64 }, (_, i) => Math.sin(i * 0.3) * Math.cos(i * 0.7)));
        }
      } catch {
        setEmbedding(Array.from({ length: 64 }, (_, i) => Math.sin(i * 0.3) * Math.cos(i * 0.7)));
      }

      setIsLoading(false);
    })();
  }, [navigate]);

  const formatDate = (iso: string) => {
    const date = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min atras`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h atras`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d atras`;
    return date.toLocaleDateString('pt-BR');
  };

  const activityStatusConfig: Record<string, { variant: 'success' | 'warning' | 'danger'; label: string }> = {
    success: { variant: 'success', label: 'OK' },
    failure: { variant: 'danger', label: 'Falha' },
    warning: { variant: 'warning', label: 'Atencao' },
  };

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <header className="border-b border-bg-surface/50 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo markSize="h-8" wordClass="text-base" />
          </div>
          <nav className="flex items-center gap-6 text-sm font-sans">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-accent-brand font-medium"
            >
              Dashboard
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="text-txt-muted hover:text-txt-primary transition-colors"
            >
              Configuracoes
            </button>
            <button
              onClick={() => {
                apiClient.clearTokens();
                navigate('/');
              }}
              className="text-txt-muted hover:text-accent-danger transition-colors"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Loading state */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
            <div className="w-12 h-12 border-4 border-bg-surface border-t-accent-brand rounded-full animate-spin mb-4" />
            <p className="text-sm text-txt-muted font-sans">Carregando dados...</p>
          </div>
        )}

        {!isLoading && <>
        {/* Top section: RhythmPrint + Profile Health */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* RhythmPrint */}
          <div className="lg:col-span-1 flex flex-col items-center p-6 bg-bg-secondary border border-bg-surface rounded-card">
            <h3 className="text-sm font-medium text-txt-primary font-sans mb-4">
              Seu RhythmPrint
            </h3>
            <RhythmPrintViz
              embedding={embedding}
              size={200}
              color="#7c5cfc"
              secondaryColor="#4fc3f7"
            />
            <div className="mt-4 text-center">
              <StatusBadge label="Perfil Ativo" variant="success" pulse />
            </div>
          </div>

          {/* Profile Health + Stats */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Profile Health */}
            <div className="flex flex-col items-center justify-center p-6 bg-bg-secondary border border-bg-surface rounded-card">
              <h3 className="text-sm font-medium text-txt-primary font-sans mb-4">
                Saude do Perfil
              </h3>
              <ProgressRing
                value={profileHealth ?? 0}
                size={120}
                strokeWidth={8}
                color={(profileHealth ?? 0) >= 80 ? '#00d26a' : (profileHealth ?? 0) >= 60 ? '#f5a623' : '#e74c3c'}
              />
              <p className="text-xs text-txt-muted font-sans mt-3 text-center">
                {profileHealth === null
                  ? 'Carregando...'
                  : profileHealth >= 80
                    ? 'Perfil robusto e confiavel'
                    : profileHealth >= 60
                      ? 'Considere fazer mais sessoes'
                      : 'Perfil precisa de reforco'}
              </p>
            </div>

            {/* Key Metrics */}
            <div className="p-6 bg-bg-secondary border border-bg-surface rounded-card">
              <h3 className="text-sm font-medium text-txt-primary font-sans mb-4">
                Metricas Gerais
              </h3>
              {metrics ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-txt-muted font-sans">Score medio</span>
                  <span className="text-sm font-mono font-bold text-accent-brand">
                    {Math.round(metrics.avgScore)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-txt-muted font-sans">Total de logins</span>
                  <span className="text-sm font-mono font-bold text-txt-primary">
                    {metrics.totalLogins}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-txt-muted font-sans">Taxa de sucesso</span>
                  <span className="text-sm font-mono font-bold text-accent-success">
                    {(metrics.successRate * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-txt-muted font-sans">WPM medio</span>
                  <span className="text-sm font-mono font-bold text-accent-info">
                    {Math.round(metrics.avgWpm)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-txt-muted font-sans">Precisao media</span>
                  <span className="text-sm font-mono font-bold text-accent-success">
                    {(metrics.avgAccuracy * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-txt-muted font-sans">Idade do perfil</span>
                  <span className="text-sm font-mono text-txt-primary">
                    {metrics.profileAge} dias
                  </span>
                </div>
              </div>
              ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-xs text-txt-muted font-sans">Sem dados disponiveis</p>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-bg-secondary border border-bg-surface rounded-card p-6 mb-8">
          <h3 className="text-sm font-medium text-txt-primary font-sans mb-4">
            Atividade Recente
          </h3>
          <div className="space-y-3">
            {activity.length === 0 ? (
              <p className="text-xs text-txt-muted font-sans text-center py-6">Nenhuma atividade registrada</p>
            ) : activity.map((entry) => {
              const statusCfg = activityStatusConfig[entry.status];
              return (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-3 bg-bg-primary/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <StatusBadge label={statusCfg.label} variant={statusCfg.variant} />
                    <div>
                      <span className="text-sm text-txt-primary font-sans">
                        {entry.details}
                      </span>
                      <span className="block text-xs text-txt-muted font-sans">
                        {entry.device}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    {entry.score !== undefined && (
                      <span className="block text-sm font-mono font-bold text-accent-brand">
                        {entry.score}%
                      </span>
                    )}
                    <span className="block text-xs text-txt-muted font-sans">
                      {formatDate(entry.timestamp)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/login')}
            className="p-4 bg-bg-secondary border border-bg-surface rounded-card hover:border-accent-brand/30 transition-all text-left group"
          >
            <span className="text-lg mb-2 block">🔐</span>
            <span className="text-sm font-medium text-txt-primary font-sans group-hover:text-accent-brand transition-colors">
              Testar Login
            </span>
            <span className="block text-xs text-txt-muted font-sans mt-0.5">
              Verifique sua assinatura
            </span>
          </button>
          <button
            onClick={() => navigate('/enrollment')}
            className="p-4 bg-bg-secondary border border-bg-surface rounded-card hover:border-accent-brand/30 transition-all text-left group"
          >
            <span className="text-lg mb-2 block">🔄</span>
            <span className="text-sm font-medium text-txt-primary font-sans group-hover:text-accent-brand transition-colors">
              Refinar Perfil
            </span>
            <span className="block text-xs text-txt-muted font-sans mt-0.5">
              Adicione mais sessoes
            </span>
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="p-4 bg-bg-secondary border border-bg-surface rounded-card hover:border-accent-brand/30 transition-all text-left group"
          >
            <span className="text-lg mb-2 block">{'\u2699'}</span>
            <span className="text-sm font-medium text-txt-primary font-sans group-hover:text-accent-brand transition-colors">
              Configuracoes
            </span>
            <span className="block text-xs text-txt-muted font-sans mt-0.5">
              Seguranca e privacidade
            </span>
          </button>
        </div>
        </>}
      </main>

      {/* Continuous Auth Widget */}
      <ContinuousAuthWidget />
    </div>
  );
}
